import { getAnalysis } from "@/store/services/task/actions/analysisService";
import { getDecision } from "@/store/services/task/actions/decisionService";
import { SummaryRating } from "@/types";

export function asResourceUid(value: unknown): string | undefined {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || undefined;
  }
  if (typeof value === "object") {
    const raw = value as { value?: unknown; resourceUid?: unknown; id?: unknown };
    const nested = raw.value ?? raw.resourceUid ?? raw.id;
    if (typeof nested === "string" && nested.trim()) {
      return nested.trim();
    }
  }
  return undefined;
}

export function unwrapBusinessData(raw: unknown): Record<string, any> {
  if (raw === undefined || raw === null || raw === "") {
    return {};
  }
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }
  if (typeof raw === "object") {
    return raw as Record<string, any>;
  }
  return {};
}

export function extractAnalysisProcessIdFromDecision(decision: any): string | undefined {
  const processes = decision?.associatedAnalysisProcess;
  if (!Array.isArray(processes) || processes.length === 0) {
    return undefined;
  }
  const last = processes[processes.length - 1];
  return asResourceUid(last?.analysisProcess)
    || asResourceUid(last?.analysisProcessResourceUid)
    || asResourceUid(last?.analysis_process_resourceuid);
}

export function extractAnalysisProcessIdFromDecisionSteps(decisionSteps: unknown): string | undefined {
  if (!Array.isArray(decisionSteps)) {
    return undefined;
  }
  for (let i = decisionSteps.length - 1; i >= 0; i--) {
    const processId = extractAnalysisProcessIdFromDecision(decisionSteps[i]);
    if (processId) {
      return processId;
    }
  }
  return undefined;
}

export function extractDecisionIdFromProposalDecisions(proposalDecisions: unknown): string | undefined {
  if (!Array.isArray(proposalDecisions) || proposalDecisions.length === 0) {
    return undefined;
  }
  const last = proposalDecisions[proposalDecisions.length - 1];
  return asResourceUid(last?.decision) || asResourceUid(last);
}

export async function resolveAnalysisProcessIdFromOffer(offer: any, decisionSteps?: unknown): Promise<string | undefined> {
  const fromSteps = extractAnalysisProcessIdFromDecisionSteps(decisionSteps);
  if (fromSteps) {
    return fromSteps;
  }
  const fromOfferDecisions = extractAnalysisProcessIdFromDecisionSteps(offer?.proposals?.[0]?.proposalDecisions);
  if (fromOfferDecisions) {
    return fromOfferDecisions;
  }
  const decisionId = extractDecisionIdFromProposalDecisions(offer?.proposals?.[0]?.proposalDecisions);
  if (!decisionId) {
    return undefined;
  }
  const decision = await getDecision(decisionId);
  return extractAnalysisProcessIdFromDecision(decision);
}

export function mapAnalysisRatings(ratings: unknown): SummaryRating[] {
  if (!Array.isArray(ratings)) {
    return [];
  }
  return ratings
    .filter((rating: any) => !rating?.validity?.until)
    .map((rating: any) => ({
      type: rating?.type,
      calculatedValue: rating?.calculatedValue,
      updatedValue: rating?.updatedValue,
      decision: rating?.decision,
      comment: rating?.comment,
    }));
}

export async function fetchSummaryRatings(processId: string): Promise<SummaryRating[]> {
  const analysis = await getAnalysis(processId);
  return mapAnalysisRatings(analysis?.analysisRatings);
}
